/**
 * Device answers as both backends send them, and as the app holds them. The cloud's row is the
 * media_server's device as the server last synced it, with Go's omitempty applied; a live
 * answer is the media_server's own. See docs/rules/api_boundary.md.
 *
 * DiscoveryState is no answer: it is what the add form holds between a probe and the add.
 */
import { z } from 'zod';

import { keyBy, listOf, recordOf } from '@/api/listOf';
import { declareMediaServerDefault } from '@/api/mediaServerFill';
import { SECOND_MS } from '@/lib/timeUnits';

import { DeviceIdSchema } from './deviceId';
import {
    CreateDeviceWebSessionResponse as DeviceWebSessionSchema,
    Device as GeneratedDeviceSchema,
    DeviceBase as GeneratedDeviceBaseSchema,
} from './generated/vms_database.zod';
import type { ResourceStatus } from './notifications';
import { ResourceStatusSchema } from './notifications';

/** How long a server's storage state takes to follow a change of what a camera records. */
export const DEVICE_CHANGE_PROPAGATION_MS = 5 * SECOND_MS;

/** The quarter turns a camera's picture can be rotated by. */
export const ROTATION_DEGREES = [0, 90, 180, 270] as const;
export type RotationDegrees = (typeof ROTATION_DEGREES)[number];

/** What a device is; the media_server decides it from its driver. */
export const DeviceType = {
    Camera: 'camera',
    Io: 'io',
} as const;
export type DeviceType = typeof DeviceType[keyof typeof DeviceType];

// The media_server's device types grow with its capability model (a speaker, a doorbell). Both
// backends treat a type they do not know as a camera, and so does the UI.
const DeviceTypeSchema = z.enum(DeviceType).catch(DeviceType.Camera);

export const DeviceBaseSchema = GeneratedDeviceBaseSchema.extend({
    deviceType: declareMediaServerDefault(DeviceTypeSchema, DeviceType.Camera),
});
export type DeviceBase = z.infer<typeof DeviceBaseSchema>;

/** One stream's encoder parameters, as the device reports and takes them. */
export const StreamConfigSchema = z.looseObject({
    codec: z.string(),
    width: z.number(),
    height: z.number(),
    fps: z.number(),
    bitrate: z.number(),
    govLength: z.number(),
    /** A named tier makes the server compute the bitrate; empty is the explicit Kbps. */
    quality: z.string(),
});
export type StreamConfig = z.infer<typeof StreamConfigSchema>;

/**
 * One encoder setting a camera says it can produce. A list of these bounds what the video
 * form offers; fps is reported as a maximum, not a list.
 */
export const EncoderOptionSchema = z.object({
    codec: z.string(),
    width: z.number(),
    height: z.number(),
    fps: z.number(),
});
export type EncoderOption = z.infer<typeof EncoderOptionSchema>;

/** One profile the camera offers, as its own web UI names it. */
export const StreamProfileSchema = z.object({
    token: z.string(),
    name: z.string(),
    url: z.string(),
    config: StreamConfigSchema,
    encoderToken: z.string(),
    /** What this profile's encoder can produce. */
    options: listOf(EncoderOptionSchema, 'options'),
});
export type StreamProfile = z.infer<typeof StreamProfileSchema>;

/** When a camera records, and how much it keeps either side of an event. */
export const RecordingSettingsSchema = z.looseObject({
    mode: z.string(),
    preRecordSec: z.number().optional(),
    postRecordSec: z.number().optional(),
    // A cloud row written before the UI kept this field has none; the media_server reads an
    // absent or empty list as every category.
    eventCategories: z.array(z.string()).optional(),
});
export type RecordingSettings = z.infer<typeof RecordingSettingsSchema>;

/** Set while a camera runs on hardware that replaced its original one. */
export const DeviceReplacementSchema = z.object({
    replacedAtMs: z.number(),
    /** The hardware the camera runs on now. */
    hardwareId: z.string(),
    /** Where the camera's original hardware was reached. */
    previousUrl: z.string(),
    /** The original hardware's MAC, 12 lowercase hex digits; empty when it was never learned. */
    previousMac: z.string(),
});
export type DeviceReplacement = z.infer<typeof DeviceReplacementSchema>;

/** A device as either backend sends it; a write sends part of one. */
export const DeviceDtoSchema = GeneratedDeviceSchema.extend({
    base: DeviceBaseSchema,
    enabledAgents: z.array(z.string()).optional(),
    blurCategories: z.array(z.string()).optional(),
    /** The media_server's own; the UI passes it through. */
    params: z.unknown().optional(),
    recordingSettings: RecordingSettingsSchema.optional(),
    primaryStream: StreamConfigSchema.optional(),
    secondaryStream: StreamConfigSchema.optional(),
    availableProfiles: listOf(StreamProfileSchema, 'availableProfiles',
        { getKey: keyBy('token') })
        .optional(),
    /** Stream-level encoder options, for cameras whose profiles carry none. */
    primaryCapabilities: listOf(EncoderOptionSchema, 'primaryCapabilities').optional(),
    secondaryCapabilities: listOf(EncoderOptionSchema, 'secondaryCapabilities').optional(),
    /** Device ids of the same-server two-way cameras this camera's talk routes to. Unset defaults
     *  to the camera itself when it is two-way, else empty. */
    talkTargets: z.array(DeviceIdSchema).optional(),
    /** Read-only, server-computed: the enabled two-way cameras on this camera's server that a talk
     *  may target - the set a talk-target picker offers. Never sent in a PATCH. */
    eligibleTalkTargets: z.array(DeviceIdSchema).optional(),
    replacement: DeviceReplacementSchema.optional(),
    /** Only the media_server's answers carry it. */
    status: ResourceStatusSchema.optional(),
});
export type DeviceDto = z.infer<typeof DeviceDtoSchema>;

/**
 * A device as the app holds one: `base` hoisted beside `name`, and the values Go leaves out
 * when empty written back. An optional field here is one whose absence means something.
 */
export interface FlatDevice extends DeviceBase {
    name: string;
    serverId: string;
    primaryUrl: string;
    secondaryUrl: string;
    recordingEnabled: boolean;
    enabledAgents: string[];
    blurCategories: string[];
    params?: unknown;
    recordingSettings?: RecordingSettings;
    primaryStream?: StreamConfig;
    secondaryStream?: StreamConfig;
    availableProfiles: StreamProfile[];
    primaryProfileToken: string;
    secondaryProfileToken: string;
    optimizeSettings: boolean;
    primaryCapabilities?: EncoderOption[];
    secondaryCapabilities?: EncoderOption[];
    rotationDegrees: RotationDegrees;
    audioEnabled: boolean;
    hasTwoWayAudio: boolean;
    talkEnabled?: boolean;
    talkTargets?: string[];
    eligibleTalkTargets?: string[];
    replacement?: DeviceReplacement;
    /** Absent until the media_server answers with it or the stream reports it. */
    status?: ResourceStatus;
}

/** The cameras as one fetch leaves them. `isLoaded` turns true at the first list and stays. */
export interface DevicesSnapshot {
    devices: FlatDevice[];
    isLoading: boolean;
    isLoaded: boolean;
    error: Error | null;
}

/** Which camera a member of the live camera family holds, and the server it asks. */
export interface LiveCameraKey {
    cameraId: string;
    serverId: string;
}

/** One camera as its media_server reports it now: capabilities and profiles included. */
export interface LiveCameraSnapshot {
    camera: FlatDevice | null;
    isLoading: boolean;
    error: Error | null;
}

/**
 * The cameras that the camera settings panel edits. A reload leaves `isLoading` false, so the
 * sections stay mounted with their drafts.
 */
export interface EditedCameras {
    cameras: FlatDevice[];
    isLoading: boolean;
}

/** The talk and audio fields a camera settings save sends to the media_server. */
export interface TalkEdit {
    talkEnabled?: boolean;
    talkTargets?: string[];
    audioEnabled?: boolean;
}

/** camera id → the last name the device list gave it. */
export type CameraNames = Readonly<Record<string, string>>;

/** The cameras by id and their names, for a lookup in a loop or a callback. */
export interface CameraIndex {
    /** The listed cameras by id, with this session's pending writes laid over them. */
    byId: ReadonlyMap<string, FlatDevice>;
    /** The camera's name, else the last one it was listed under. */
    getName: (id: string) => string | undefined;
}

/**
 * What a device write sends. `base` is partial too: the add form fills only the fields
 * discovery found, and the server supplies the rest.
 */
export type DeviceDraft = Partial<Omit<DeviceDto, 'base'>> & { base?: Partial<DeviceBase> };

/**
 * A throwaway origin that proxies one camera's web root for about an hour, as
 * `POST /devices/{id}/web-sessions` answers.
 */
export { DeviceWebSessionSchema };
export type DeviceWebSession = z.infer<typeof DeviceWebSessionSchema>;

/** One stream's encoder fields as the video form holds them, all strings. */
export interface StreamFields {
    codec: string;
    resolution: string;
    fps: string;
    govLength: string;
    bitrate: string;
    quality: string;
}

/**
 * What the video settings read off a camera: every field optional, because a
 * camera reports the parts its driver knows and the section shows what it got.
 * A write needs the id too, which the composer asks for on top.
 */
export interface VideoCamera {
    id?: string;
    serverId?: string;
    primaryUrl?: string;
    secondaryUrl?: string;
    primaryStream?: StreamConfig | null;
    secondaryStream?: StreamConfig | null;
    availableProfiles?: StreamProfile[];
    primaryProfileToken?: string;
    secondaryProfileToken?: string;
    /** Stream-level encoder options, for cameras whose profiles carry none. */
    primaryCapabilities?: EncoderOption[];
    secondaryCapabilities?: EncoderOption[];
}

/** What one video Apply sends for a camera's two streams. */
export interface VideoPatch {
    primaryProfileToken: string;
    secondaryProfileToken: string;
    primaryStream: StreamConfig;
    secondaryStream: StreamConfig;
}

/** One camera's video write: the patch, and the server that must take it. */
export interface VideoWrite {
    cameraId: string;
    serverId?: string;
    patch: VideoPatch;
}

export type FrameQuality = 'primary' | 'secondary';

/** Stream selector for a still: the high-res primary or the sub-stream. */
export type ImageQuality = 'low' | 'high';

export interface CropRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export type FramePick = 'roundLeft' | 'exact' | 'roundRight';

/** A camera's preview as a `data:` URL. `fetchedAtMs` is null when storage kept no time. */
export interface StoredPreview {
    dataUrl: string;
    fetchedAtMs: number | null;
}

/** Options for deviceImageUrl; buildImageQuery in ./client documents each field. */
export interface ImageOpts {
    serverId?: string;
    position?: number;
    exact?: FramePick;
    width?: number;
    height?: number;
    crop?: CropRect | null;
}

/** Options for clipDownloadUrl; see buildClipDownloadPath in ./client. */
export interface ClipDownloadOpts {
    serverId?: string;
    startMs: number;
    endMs: number;
    quality?: 'high' | 'low';
}

/** Options for getDeviceFrames; see buildFramesQuery in ./client. */
export interface FramesOpts {
    serverId?: string;
    startTimeMs: number;
    endTimeMs: number;
    sampleIntervalMs: number;
    quality?: FrameQuality;
    width?: number;
    crop?: CropRect;
}

/**
 * The segment list's compact encoding: the first run's start and duration, then a gap and a
 * duration for each run after it; a run still recording lasts -1. parseCompactSegments reads
 * it.
 */
export const CompactSegmentsSchema = z.array(z.number());
export type CompactSegments = z.infer<typeof CompactSegmentsSchema>;

export interface SegmentOpts {
    startTimeMs?: number;
    endTimeMs?: number;
    serverId?: string;
    /** Ends the request on the relay too; the default deadline still applies. */
    signal?: AbortSignal;
}

/** One address for discoverCameras to probe: an ONVIF host/port or an RTSP URL. */
export interface DiscoverEntry {
    host?: string;
    port?: number;
    rtspUrl?: string;
    user?: string;
    password?: string;
}

/** One probe outcome; the media_server answers one per entry, in the order asked. */
export const DiscoverResultSchema = z.object({
    address: z.string(),
    status: z.enum(['found', 'not_found']),
    /** Why nothing was found, such as 'auth_failed' or 'rtsp_unreachable'; empty when found. */
    reason: z.string(),
    /**
     * Only the fields the address gave up, and no credentials; the server's defaults when
     * nothing was found. The add that follows sends it as its draft.
     */
    device: DeviceDtoSchema,
});
export type DiscoverResult = z.infer<typeof DiscoverResultSchema>;

export const DiscoverAnswerDtoSchema = z.object({
    results: listOf(DiscoverResultSchema, 'results'),
});
export type DiscoverAnswerDto = z.infer<typeof DiscoverAnswerDtoSchema>;

/** One improvement the server reports for a camera. */
export const SuggestionSchema = z.object({
    id: z.string(),
    stream: z.enum(['primary', 'secondary', 'device']),
    message: z.string(),
    /** The whole stream config the server would write; no codec means advisory only. */
    proposed: StreamConfigSchema,
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

/** Each camera's suggestions by its id; a healthy server answers `{}`. */
export const DeviceSuggestionsSchema = recordOf(
    listOf(SuggestionSchema, 'suggestions', { getKey: keyBy('id') }),
    { keySchema: DeviceIdSchema },
);
export type DeviceSuggestions = z.infer<typeof DeviceSuggestionsSchema>;

/** A server's suggestions as the store holds them; null until they load. */
export interface SuggestionsSnapshot {
    suggestions: DeviceSuggestions | null;
    isLoading: boolean;
    error: Error | null;
}

/** One sample of a `/frames` batch: the moment asked for and the key frame served for it. */
export const DeviceFrameDtoSchema = z.object({
    requestedTimestampMs: z.number(),
    frameTimestampMs: z.number(),
    mimeType: z.string(),
    /** Base64. */
    image: z.string().min(1),
});

/** A sample the archive cannot serve, in a recording gap above all, is left out. */
export const DeviceFramesDtoSchema = z.object({
    frames: listOf(DeviceFrameDtoSchema, 'frames', { getKey: keyBy('requestedTimestampMs') }),
});
export type DeviceFramesDto = z.infer<typeof DeviceFramesDtoSchema>;

/**
 * One sample of a `/frames` batch: the moment asked for, the key frame served
 * for it (at or before), and the picture as an `<img src>`.
 */
export interface DeviceFrame {
    requestedTimestampMs: number;
    frameTimestampMs: number;
    url: string;
}

/**
 * Where the add-camera form's Find step stands. Not a wire shape: it is what
 * the form holds between a probe and the add that follows, so the tabs and the
 * result panel read one answer.
 */
export type DiscoveryState =
    { status: 'finding' } |
    { status: 'found'; device: DeviceDto } |
    { status: 'not_found'; reason?: string };
